import { Collection } from '@commerce/types/collection'
import AlgoliaCollection from '@components/collection/AlgoliaCollection'
import { Breadcrumbs } from '@components/common'
import { Minus, Plus } from '@components/icons'
import SearchClient from '@lib/algolia'
import cn from 'clsx'
import Image from 'next/legacy/image'
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BreadcrumItem } from 'types/Common'

interface Props {
  collection: any
  prismicBrandPageData: any
  collectionHandle: string
  searchState: any
}

interface BrandImageProps {
  dimensions: {
    width: number
    height: number
  }
  url: string
}

interface BrandListItemProps {
  brandImage?: BrandImageProps
  brandLink: string
  brandName: string
}

interface BrandListProps {
  items: [BrandListItemProps]
  slice_type: string
}

interface BrandPageDataProps {
  slices: [BrandListProps]
}

const BrandCollection: FC<Props> = ({
  collection,
  prismicBrandPageData,
  collectionHandle,
  searchState,
}) => {
  const { name, descriptionHtml }: Collection = collection || {}
  const brandPageData: BrandPageDataProps | null =
    (prismicBrandPageData as any) || null
  const [readMore, setReadMore] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const [refKey, setRefKey] = useState(0)

  const descRef = useRef<HTMLDivElement>(null)

  const expandableLink = readMore ? (
    <div className="flex items-center justify-center hover:underline underline-offset-1 cursor-pointer pt-4">
      Read Less <Minus className="ml-2" />
    </div>
  ) : (
    <div className="flex items-center justify-center hover:underline underline-offset-1 cursor-pointer pt-4">
      Read More <Plus className="ml-2" />
    </div>
  )

  const expandableText = !readMore ? 'ReadMore' : 'ReadLess'

  // for brand banner image
  const brandList: BrandListProps | null =
    brandPageData?.slices?.find(
      (slice: BrandListProps) => slice.slice_type === 'brand_list'
    ) || null

  const theBrand: BrandListItemProps | null =
    brandList?.items.find((item: BrandListItemProps) =>
      item.brandLink?.includes(collectionHandle)
    ) || null

  const brandBannerImage = theBrand?.brandImage

  const algoliaIndex = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_PRIMARY_INDEX!

  const breadcrumbOptions: BreadcrumItem[] = [
    { link: '/brands', label: 'Brands' },
  ]

  useEffect(() => {
    setReadMore(false)
    setRefKey(refKey + 1)
  }, [descriptionHtml])

  useLayoutEffect(() => {
    if (descRef.current) {
      setShowMore(descRef.current.scrollHeight > descRef.current.clientHeight)
    }
  }, [descRef.current, refKey])

  return (
    <>
      <div className="wrapper">
        <div className="pb-10">
          <div>
            <Breadcrumbs current={name} options={breadcrumbOptions} />
          </div>
          {brandBannerImage?.url && (
            <div className="pb-8">
              <Image
                src={brandBannerImage.url}
                width={brandBannerImage.dimensions?.width}
                height={brandBannerImage.dimensions?.height}
                alt={name}
                unoptimized
              />
            </div>
          )}
          <h1 className="text-text-dark uppercase font-normal text-center text-mobileh4 lg:text-h4">
            {name}
          </h1>

          {descriptionHtml && (
            <div className="text-text-subdued font-normal text-center text-body1 lg:text-body1 mt-6 w-full lg:w-10/12 mx-auto">
              <div
                ref={descRef}
                className={cn(
                  !readMore && 'line-clamp-4 md:line-clamp-3 lg:line-clamp-2',
                  'rte-content collection-seo'
                )}
                dangerouslySetInnerHTML={{
                  __html: descriptionHtml.replace('/break/', ''),
                }}
              />
              {showMore && (
                <a
                  onClick={() => {
                    setReadMore(!readMore)
                  }}
                  title={expandableText}
                >
                  {expandableLink}
                </a>
              )}
            </div>
          )}
        </div>
        <AlgoliaCollection
          searchClient={SearchClient}
          indexName={algoliaIndex}
          searchState={searchState}
          defaultCategory={collectionHandle}
        />
      </div>
    </>
  )
}

export default BrandCollection
